// src/views/Pipeline.jsx
// Extracted from App.jsx — behavior-preserving split (2026-07-20).
// Perf: lanes and rows are memoized so hover/drag state only re-renders the
// affected lane instead of the whole board.
// 10/1/26: one lane per stage of the simplified STAGES list; the Prospect lane
// also carries every due call back (display only — see CallbackRow); "Add a
// deal" takes a deal priced outside the calculator (see AddDealModal).
// 10/2/26: Max's own pipeline (access.ownFilesOnly — src/access.js). His
// board holds only files he owns: no Mine · Max · All toggle, no owner chips,
// no Re-price Radar, no New Call or Automations, and the Prospect lane's call
// backs come from HIS files only — never a mail-list lead. "+ New lead"
// (NewLeadModal) types a lead straight in as a Prospect file he owns, and
// "Add a deal" is his too.
// 10/9/26: interest levels (src/interest.js) — every file row / call back file
// card / Fallout row has the dot (InterestMenu) to set how Reece feels about
// it and the Booked switch; the name takes the feeling's colour and a booked
// row the yellow highlight. InterestBar (above the lanes) counts each level
// and filters the lanes by it (remembered per browser).
// 10/10/26: the row's hover preview waits ~350 ms on the row's body, never
// opens / closes at once over a control (💬 note, the dot, ☰, any button /
// link / field) and sits BESIDE the row, never over it (see HOVER PREVIEW);
// the lanes sort least → most interested by default (remembered, SORT_KEY);
// "Booked leads (N)" in the header = the Booked chip, one click.

import React, { useState, useMemo, useRef, useCallback, useEffect, useLayoutEffect } from "react";
import { C, Ic } from '../theme';
import { Badge, Btn, CallBtn, Hdr, Inp, MoneyInp } from '../ui';
import { FALLOUT_DENIED, FALLOUT_NOAPP, FALLOUT_QUAL, FALLOUT_STAGES, LOCK_STAGE, MANUAL_DEAL_LABEL, HS2_LOAN_TYPE, MANUAL_DEAL_LENDERS, MANUAL_DEAL_PROGRAMS, STAGES, STC } from '../constants';
import { ageFromDobStr, currentSwap, fmtDob, fmtWhen, isDueBy, isFallout, isHs2File, isoDatePlus, loanAge, manualDealFile, manualDealWarnings, newLeadFile, ownerOf, prospectFromLead, prospectFromLoan, prospectLaneCallbacks, stageAtOrPast, topTodos } from '../lib';
import { logScenario, scenarioRow } from '../scenarioStore';
import { STAFF } from '../supabase';
import { staffAccess, usePractice } from '../access';
import { WorkQueues, OwnerChip, claimLoan } from './Dashboard';
import { RepriceRadar } from './RepriceRadar';
import { fmtMoney, owedTotal } from '../reimbursements';
import { BookedLeadsToggle, BookedStrip, InterestBar, InterestMenu } from './Interest';
import { applyInterest, interestFilterActive, interestFilterMatch, interestNameStyle, interestStyle, interestSummary, readInterestFilter, sortByInterest, toggleInterestFilter, writeInterestFilter } from '../interest';
import { payCents } from '../calcRules';
import { useOwnershipUi } from '../features';

// ═══════ WHOSE FILES — Mine · <other> · All ═══════
// Reece and Max share one pipeline; `owner` is a tracking tag ("whoever works
// it owns it"), not privacy. The toggle filters the active board and the
// Re-price Radar. Remembered per browser — stored as the RELATIVE choice, so
// "mine" is Reece on his machine and Max on his. The Fallout board is COMBINED
// for both users (Reece's ask): it opens on All every time, with an owner chip
// on each row; narrowing it is a separate, unremembered choice on that board.
// 10/10/26: all of this only while "Lead assignment & Max on files" is on
// (src/features.js — off by default). Off: no toggle, no chips, no "Owner"
// line in the hover preview, and every board shows every file as if "All"
// were picked; the remembered pick is left alone for when it comes back on.
const OWNER_VIEW_KEY="loanflow_owner_view";
const OWNER_VIEWS=["mine","other","all"];
function readOwnerView(){try{var v=localStorage.getItem(OWNER_VIEW_KEY);return OWNER_VIEWS.indexOf(v)>=0?v:"mine";}catch(e){return"mine";}}
function writeOwnerView(v){try{localStorage.setItem(OWNER_VIEW_KEY,v);}catch(e){}}
function otherStaff(me){return me==="max"?"reece":"max";}
// Is a record owned by `owner` ("reece"|"max") shown in view `v` for `me`?
function ownerInView(owner,v,me){
  if(v==="all")return true;
  return v==="mine"?owner===me:owner===otherStaff(me);
}
function OwnerToggle({me,view,setView,counts}){
  const other=otherStaff(me);
  const opts=[{v:"mine",l:"Mine",o:me},{v:"other",l:STAFF[other].name,o:other},{v:"all",l:"All",o:null}];
  return(<div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
    <span style={{fontSize:10,fontWeight:800,color:C.textDim,letterSpacing:.6,textTransform:"uppercase"}}>Whose files</span>
    <div role="group" aria-label="Whose files" style={{display:"inline-flex",alignItems:"center",padding:3,borderRadius:11,border:"1px solid "+C.border,background:C.surfaceAlt,gap:2}}>
      {opts.map(function(o){
        const on=view===o.v;
        return <button key={o.v} onClick={function(){setView(o.v);}} aria-pressed={on} title={o.v==="all"?"Both pipelines together":o.v==="mine"?"Files you work ("+STAFF[me].name+")":STAFF[other].name+"'s files"}
          style={{display:"inline-flex",alignItems:"center",gap:7,padding:"7px 13px",borderRadius:8,border:"1px solid "+(on?C.accent+"55":"transparent"),cursor:"pointer",fontSize:12.5,fontWeight:800,fontFamily:"'Mulish',system-ui,sans-serif",background:on?C.card:"transparent",color:on?C.text:C.textMuted,boxShadow:on?"0 1px 5px rgba(11,42,74,0.14)":"none",whiteSpace:"nowrap"}}>
          {o.o?<OwnerChip owner={o.o} size={18}/>:<span style={{display:"inline-flex"}}><OwnerChip owner="reece" size={15}/><span style={{marginLeft:-5,display:"inline-flex"}}><OwnerChip owner="max" size={15}/></span></span>}
          {o.l}
          {counts&&counts[o.v]!=null&&<span style={{fontSize:11,fontWeight:700,color:on?C.accentLight:C.textDim}}>{counts[o.v]}</span>}
        </button>;
      })}
    </div>
  </div>);
}
// {mine, other, all} for a list of loans.
function countOwners(pool,me){
  var c={mine:0,other:0,all:pool.length};var o2=otherStaff(me);
  pool.forEach(function(l){var o=ownerOf(l);if(o===me)c.mine++;else if(o===o2)c.other++;});
  return c;
}

// ═══════ SORT — remembered per browser (10/10/26) ═══════
// Reece: "automatically filter the statuses based on the least interested, to
// most interested, based on the colors" — "Interest (least → most)" is the
// default (src/interest.js sortByInterest; ties keep the original order). The
// last pick is remembered on this browser; any older pick still works, junk →
// the default.
const SORT_KEY="loanflow_pipeline_sort";
const DEFAULT_SORT="interest";
const SORT_OPTIONS=[
  {v:"interest",l:"Interest (least → most)",t:"Don't feel good → On the fence → Not set → Feel good; not booked before booked"},
  {v:"default",l:"Original order",t:"The board's old order — call backs due first in Prospect, then as the files were added"},
  {v:"close_asc",l:"Close ↑"},
  {v:"close_desc",l:"Close ↓"},
  {v:"comm_desc",l:"Commission ↓"},
  {v:"name_asc",l:"Name A–Z"},
];
function readSort(){try{var v=localStorage.getItem(SORT_KEY);return SORT_OPTIONS.some(function(o){return o.v===v;})?v:DEFAULT_SORT;}catch(e){return DEFAULT_SORT;}}
function writeSort(v){try{localStorage.setItem(SORT_KEY,v);}catch(e){/* storage blocked — the pick just isn't remembered */}}

// ═══════ HOVER PREVIEW (10/10/26) ═══════
// Reece: "if I'm trying to add a note, it needs to be separate from the
// hovering on the deal so the pop up doesn't cover up the option to click on
// adding a note". The preview opens only after HOVER_DELAY_MS on the row's
// name / body — never while the pointer is on a control (or a control has
// keyboard focus) — closes at once when the pointer reaches a control or
// leaves the row, and sits BESIDE the row (right, else left; with no room on
// either side — the lanes are full width — just below it, else above), so it
// never covers the row it describes. pointer-events:none: it can't take a
// click meant for anything under it.
const HOVER_DELAY_MS=350;
const PREVIEW_W=364;          // border-box: the old 330 of content + padding + border
const PREVIEW_EST_H=340;
const ROW_CONTROL_SEL='button,a,input,textarea,select,label,summary,[role="button"],[role="link"],[role="checkbox"],[role="switch"],[role="menu"],[role^="menuitem"],[contenteditable="true"],[data-interest-control],[data-interest-layer],[data-row-control],[data-drag-handle]';
// Is `t` (an event target / the focused element) one of the row's controls?
// Anything outside the row's DOM that still reached its handlers came through
// a portal from one of them (the interest menu) — a control too.
function isRowControl(t,row){
  if(!t||!t.closest)return false;
  if(row&&!row.contains(t))return true;
  var c=t.closest(ROW_CONTROL_SEL);
  return !!c&&(!row||row.contains(c));
}
// How the page was last reached for — "keyboard" after a key press, "pointer"
// after a press. A control with KEYBOARD focus keeps the preview shut (it
// would sit where he's tabbing); a button just clicked doesn't.
var lastInput="pointer";
function useInputModality(){
  useEffect(function(){
    if(typeof document==="undefined")return undefined;
    var k=function(){lastInput="keyboard";};
    var p=function(){lastInput="pointer";};
    document.addEventListener("keydown",k,true);
    document.addEventListener("mousedown",p,true);
    document.addEventListener("touchstart",p,true);
    return function(){document.removeEventListener("keydown",k,true);document.removeEventListener("mousedown",p,true);document.removeEventListener("touchstart",p,true);};
  },[]);
}
function keyboardOnControl(row){
  if(lastInput!=="keyboard"||typeof document==="undefined")return false;
  var ae=document.activeElement;
  return !!(ae&&ae!==document.body&&row&&row.contains(ae)&&isRowControl(ae,row));
}
// Where the card goes: r = the row's rect, w/h = the card's size, vw/vh = the
// window, px = the pointer's x. → {left, top, side, maxH} (maxH: cap the card
// when even the roomier of above / below is short — it still never overlaps).
function hoverCardPos(r,w,h,vw,vh,px){
  var M=8,G=10;
  var top=Math.max(M,Math.min(r.top,vh-h-M));
  if(r.right+G+w<=vw-M)return{left:r.right+G,top:top,side:"right",maxH:null};
  if(r.left-G-w>=M)return{left:r.left-G-w,top:top,side:"left",maxH:null};
  var left=Math.max(M,Math.min((px!=null?px:r.left)-24,vw-w-M));
  var below=vh-M-(r.bottom+G),above=r.top-G-M;
  if(h<=below||below>=above)return{left:left,top:r.bottom+G,side:"below",maxH:h<=below?null:Math.max(0,below)};
  return{left:left,top:Math.max(M,r.top-G-h),side:"above",maxH:h<=above?null:Math.max(0,above)};
}
// The card's frame: placed from an estimate, then measured and re-placed
// before paint. z 80 — under the interest menu (950) and every modal.
function HoverPreview({id,anchor,edge,children}){
  const ref=useRef(null);
  const vw=function(){return window.innerWidth||1024;};
  const vh=function(){return window.innerHeight||768;};
  const [pos,setPos]=useState(function(){return hoverCardPos(anchor.rect,PREVIEW_W,PREVIEW_EST_H,vw(),vh(),anchor.px);});
  useLayoutEffect(function(){
    var el=ref.current;if(!el)return;
    var h=Math.max(el.offsetHeight||0,el.scrollHeight||0)||PREVIEW_EST_H;
    var p=hoverCardPos(anchor.rect,el.offsetWidth||PREVIEW_W,h,vw(),vh(),anchor.px);
    setPos(function(prev){return prev.left===p.left&&prev.top===p.top&&prev.side===p.side&&prev.maxH===p.maxH?prev:p;});
  },[anchor]);
  return(<div ref={ref} data-hover-card={id} data-hover-side={pos.side} aria-hidden="true"
    style={{position:"fixed",left:pos.left,top:pos.top,width:PREVIEW_W,maxHeight:pos.maxH!=null?pos.maxH:undefined,overflow:"hidden",boxSizing:"border-box",zIndex:80,pointerEvents:"none",background:C.surface,border:"1px solid "+edge+"70",borderRadius:12,boxShadow:"0 12px 34px rgba(11,42,74,0.28)",padding:"14px 16px"}}>
    {children}
  </div>);
}


// ═══════ PIPELINE (Vertical) ═══════

// One loan row. Memoized: re-renders only when its loan object, drag state,
// or hover state actually changes.
// "Where it stands" — a one-line comment on the board itself, so a file that
// hasn't moved in a while can say why without opening it. Stored on the loan
// as statusNote/statusNoteAt (packed by wfPack, so it survives a save).
function fmtNoteAge(iso){
  if(!iso)return"";const d=new Date(iso);if(isNaN(d.getTime()))return"";
  const days=Math.floor((Date.now()-d.getTime())/86400000);
  return days<=0?"today":days===1?"yesterday":days<30?days+"d ago":d.toLocaleDateString();
}
// callbackDue: the YYYY-MM-DD of a due call back on this file (its own, or
// the lead it came from) — a badge only; the file stays where it is.
const LoanRow = React.memo(function LoanRow({l,isLast,isDragged,isHovered,onDragStart,onDragEnd,onOpen,onHover,onUnhover,onNote,onSetInterest,onMenuLock,showOwner,callbackDue,callbackOverdue}){
  const urgent=l.tasks.some(function(t){return t.startsWith("URGENT");});
  const [editing,setEditing]=useState(false);
  // Interest dot: a press that starts on it never drags the row, and the row
  // can't be dragged while its menu is open.
  const [menuOpen,setMenuOpen]=useState(false);
  const pressOnDot=useRef(false);
  const ist=interestStyle(l);
  // ── Hover preview intent (see HOVER PREVIEW above) ──
  // `hot` = the pointer is over the row (its highlight, at once); the preview
  // itself waits HOVER_DELAY_MS on the body and is Pipeline's `hover` state
  // (isHovered = it is open for this row).
  const rowRef=useRef(null);
  const [hot,setHot]=useState(false);
  const hoverTimer=useRef(null);
  const lastX=useRef(null);
  const openRef=useRef(false);openRef.current=!!isHovered;
  const blockRef=useRef(false);blockRef.current=editing||menuOpen||!!isDragged;
  const closeHover=useCallback(function(){
    if(hoverTimer.current){clearTimeout(hoverTimer.current);hoverTimer.current=null;}
    if(openRef.current){openRef.current=false;onUnhover(l.id);}
  },[onUnhover,l.id]);
  const fireHover=function(){
    hoverTimer.current=null;
    var el=rowRef.current;
    if(!el||blockRef.current||keyboardOnControl(el))return;
    var r=el.getBoundingClientRect();
    openRef.current=true;
    onHover({id:l.id,rect:{left:r.left,right:r.right,top:r.top,bottom:r.bottom},px:lastX.current});
  };
  const trackHover=function(e){
    lastX.current=e.clientX;
    if(blockRef.current||isRowControl(e.target,rowRef.current)){closeHover();return;}
    if(openRef.current||hoverTimer.current)return;
    hoverTimer.current=setTimeout(fireHover,HOVER_DELAY_MS);
  };
  // A row that leaves the board (filtered out, moved by a poll) takes its
  // open preview with it — no mouseleave would ever come to close it.
  const closeRef=useRef(closeHover);closeRef.current=closeHover;
  useEffect(function(){return function(){closeRef.current();};},[]);
  // The open menu also holds this lane's order (Pipeline's menuLock — see
  // `lanes` there): the row stays under its menu until the menu closes.
  const onMenuOpen=useCallback(function(v){setMenuOpen(v);if(v)closeHover();if(onMenuLock)onMenuLock(l.id,v);},[closeHover,onMenuLock,l.id]);
  const setInterest=useCallback(function(patch){onSetInterest(l.id,patch);},[onSetInterest,l.id]);
  const [draft,setDraft]=useState("");
  const [saveState,setSaveState]=useState(""); // "" | "saving" | "saved"
  // ── Autosave — the comment saves as it's typed, no Save click ──
  // Debounced 700ms while typing (statusNote only, no log line), then on
  // blur / Save / Ctrl+Enter / unmount it is committed with ONE activity line
  // if it differs from what it was when editing began. Esc / Cancel puts the
  // original back. Refs so the unmount flush sees the latest values.
  const draftRef=useRef("");
  const origRef=useRef("");
  const editingRef=useRef(false);
  const timerRef=useRef(null);
  const onNoteRef=useRef(onNote);onNoteRef.current=onNote;
  const idRef=useRef(l.id);idRef.current=l.id;
  const clearTimer=function(){if(timerRef.current){clearTimeout(timerRef.current);timerRef.current=null;}};
  const commit=function(){
    if(!editingRef.current)return;
    clearTimer();editingRef.current=false;
    onNoteRef.current(idRef.current,draftRef.current.trim(),{log:true,from:origRef.current});
  };
  useEffect(function(){return function(){commit();};},[]);// eslint-disable-line react-hooks/exhaustive-deps
  const stop=function(e){e.stopPropagation();};
  const startEdit=function(e){e.stopPropagation();var cur=l.statusNote||"";origRef.current=cur;draftRef.current=cur;editingRef.current=true;setDraft(cur);setSaveState("");setEditing(true);closeHover();};
  const change=function(v){
    draftRef.current=v;setDraft(v);setSaveState("saving");clearTimer();
    timerRef.current=setTimeout(function(){timerRef.current=null;onNoteRef.current(idRef.current,draftRef.current.trim(),{log:false});setSaveState("saved");},700);
  };
  const save=function(e){if(e)e.stopPropagation();commit();setEditing(false);};
  const cancel=function(e){if(e)e.stopPropagation();clearTimer();editingRef.current=false;onNoteRef.current(idRef.current,origRef.current,{log:false});setEditing(false);};
  const clearNote=function(e){if(e)e.stopPropagation();draftRef.current="";commit();setEditing(false);};
  const noteAge=fmtNoteAge(l.statusNoteAt);
  const stale=l.statusNoteAt&&(Date.now()-new Date(l.statusNoteAt).getTime())>14*86400000;
  return(<div ref={rowRef} data-loan-row={l.id} draggable={!editing&&!menuOpen} onMouseDownCapture={function(e){pressOnDot.current=!!(e.target&&e.target.closest&&e.target.closest("[data-interest-control],[data-interest-layer]"));closeHover();}}
    onDragStart={function(e){if(editing||menuOpen||pressOnDot.current){e.preventDefault();return;}closeHover();onDragStart(e,l.id);}} onDragEnd={onDragEnd} onClick={function(){if(!editing)onOpen(l);}}
    onFocusCapture={function(e){if(isRowControl(e.target,rowRef.current))closeHover();}}
    style={{padding:"11px 20px",borderBottom:!isLast?"1px solid "+C.border:"none",cursor:editing?"default":"grab",opacity:isDragged?.4:l.archived?.6:1,transition:"opacity 0.15s",
      background:ist.booked?(hot&&!editing?C.bookedHover:C.booked):(hot&&!editing?C.cardHover:"transparent"),boxShadow:ist.booked?"inset 4px 0 0 "+C.bookedEdge:"none"}}
    onMouseEnter={function(){setHot(true);}} onMouseOver={trackHover} onMouseMove={trackHover} onMouseLeave={function(){setHot(false);closeHover();}}>
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
      <div style={{display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
        <span data-drag-handle="1" style={{fontSize:11,color:C.textDim}}>{"☰"}</span>
        {showOwner&&<OwnerChip owner={ownerOf(l)}/>}
        <span style={{fontSize:12,fontFamily:"'Mulish',system-ui,sans-serif",color:C.accentLight,minWidth:65}}>{l.id}</span>
        <span style={{display:"inline-flex",alignItems:"center",gap:7}}>
          {onSetInterest&&<InterestMenu loan={l} onSet={setInterest} onOpenChange={onMenuOpen}/>}
          <span data-interest-name={l.id} title={ist.label||undefined} style={Object.assign({fontSize:13,fontWeight:ist.level?800:600,opacity:l.archived?.6:1},interestNameStyle(l))}>{l.b}</span>
        </span>
        {/* A HomeSafe Second reads as one here too, as on Loan Detail and the
            Fallout board — also a file typed before the deal type existed
            (a hand-entered "Jumbo", a HECM file switched to HS2 on New Call). */}
        <Badge label={isHs2File(l)?HS2_LOAN_TYPE:l.type} color={C.accent} bg={C.accentGlow}/>
        {callbackDue&&<Badge label={"Callback due "+fmtDob(callbackDue)} color={callbackOverdue?C.red:C.orange} bg={(callbackOverdue?C.red:C.orange)+"18"}/>}
        {l.deal&&l.deal.manual&&<span title="The deal figures were typed from the lender's paperwork, not priced by the calculator"><Badge label={MANUAL_DEAL_LABEL} color={C.purple} bg={C.purpleBg}/></span>}
        {l.deal&&!l.deal.manual&&Number(l.deal.lenderAmount)>0&&<span title="Priced on the lender's own amount, typed over the calculator's — the comp is figured from it"><Badge label="Lender's amount" color={C.purple} bg={C.purpleBg}/></span>}
        {l.archived&&<Badge label="🗂 Archived" color={C.textDim} bg={C.textDim+"15"}/>}
        {!l.archived&&l.tasks.length>0&&<Badge label={l.tasks.length+" tasks"} color={urgent?C.red:C.orange} bg={urgent?C.redBg:C.orangeBg}/>}
        {/* Reimbursements owed to Reece on this file (deal builder, 10/10 — src/reimbursements.js) */}
        {owedTotal(l)>0&&<span data-owed-badge={l.id} title="Reimbursements owed to you on this file (credit report, appraisal…)" style={{display:"inline-flex",alignItems:"center",padding:"2px 9px",borderRadius:20,fontSize:11,fontWeight:700,color:C.clayDeep,background:C.white,border:"1px solid "+C.clay+"99",letterSpacing:.3,whiteSpace:"nowrap"}}>{fmtMoney(owedTotal(l))+" owed"}</span>}
      </div>
      <div style={{display:"flex",alignItems:"center",gap:12}}>
        <span style={{fontSize:13,fontWeight:600,color:C.gold,fontFamily:"'Mulish',system-ui,sans-serif"}}>${l.comm.toLocaleString()}</span>
        <span style={{fontSize:11,color:C.textDim}}>{l.estClose}</span>
        {!editing&&<button data-note-btn={l.id} onClick={startEdit} title={l.statusNote?"Edit the status comment":"Add a comment — where does this one stand?"} style={{border:"1px solid "+(l.statusNote?C.accent+"40":C.border),background:l.statusNote?C.accentGlow:"transparent",color:l.statusNote?C.accent:C.textDim,borderRadius:6,padding:"2px 7px",fontSize:11,cursor:"pointer",fontFamily:"'Mulish',system-ui,sans-serif"}}>💬{l.statusNote?"":" note"}</button>}
      </div>
    </div>
    {!editing&&l.statusNote&&<div data-row-control="note" onClick={startEdit} title="Click to edit" style={{marginTop:6,marginLeft:26,fontSize:12,color:C.textMuted,fontStyle:"italic",display:"flex",gap:8,alignItems:"baseline",cursor:"text"}}>
      <span style={{whiteSpace:"pre-wrap",flex:1}}>{l.statusNote}</span>
      {noteAge&&<span style={{fontSize:10,fontStyle:"normal",color:stale?C.orange:C.textDim,whiteSpace:"nowrap"}} title={stale?"This comment is over two weeks old":""}>{stale?"⚠ ":""}{noteAge}</span>}
    </div>}
    {editing&&<div data-row-control="note-editor" onClick={stop} onMouseDown={stop} style={{marginTop:8,marginLeft:26}}>
      <textarea autoFocus value={draft} onChange={function(e){change(e.target.value);}} onBlur={function(){save();}} onKeyDown={function(e){if(e.key==="Escape"){cancel(e);}if(e.key==="Enter"&&(e.metaKey||e.ctrlKey))save(e);}} placeholder="Where does this one stand? e.g. waiting on counseling cert, spouse hesitant, appraisal came in low…" rows={2} className="lf-field" style={{width:"100%",padding:"8px 10px",borderRadius:8,border:"1px solid "+C.accent+"60",background:C.surfaceAlt,color:C.text,fontSize:12.5,outline:"none",fontFamily:"'Mulish',system-ui,sans-serif",boxSizing:"border-box",resize:"vertical"}}/>
      {/* preventDefault on mousedown keeps focus in the box, so clicking a
          button doesn't blur-commit first and then act on a closed editor. */}
      <div onMouseDown={function(e){e.preventDefault();e.stopPropagation();}} style={{display:"flex",gap:6,marginTop:6,alignItems:"center",flexWrap:"wrap"}}>
        <Btn small primary onClick={save} style={{fontSize:11,padding:"5px 12px",minHeight:28}}>Done</Btn>
        <Btn small onClick={cancel} style={{fontSize:11,padding:"5px 12px",minHeight:28}}>Cancel</Btn>
        {(l.statusNote||draft)&&<Btn small onClick={clearNote} style={{fontSize:11,padding:"5px 12px",minHeight:28,color:C.red}}>Clear</Btn>}
        <span style={{fontSize:10,color:saveState==="saved"?C.green:C.textDim,fontWeight:saveState==="saved"?700:400}}>{saveState==="saving"?"Saving…":saveState==="saved"?"✓ Saved":"Saves as you type · Esc to undo"}</span>
      </div>
    </div>}
  </div>);
});

// A due call back shown in the Prospect lane — a fallout / archived file or a
// mail-list lead (see prospectLaneCallbacks in lib.js). Display only: not
// draggable, and nothing about the record changes until it is worked. A file
// opens Loan Detail; a lead opens the New Call console on that lead.
// A FILE's card carries its interest level like any file row (dot, name
// colour, the booked highlight); a mail-list lead has none.
const CallbackRow = React.memo(function CallbackRow({f,isLast,overdue,onOpen,showOwner,onSetInterest,onMenuLock}){
  const isFile=f.kind==="loan";
  const raw=f.raw||{};
  const where=isFile?(raw.archived&&!isFallout(raw)?"Archived · "+f.label:f.label):"Mail-list lead · "+f.label;
  const col=overdue?C.red:C.orange;
  const ist=interestStyle(isFile?raw:null);
  const setInterest=useCallback(function(patch){if(onSetInterest)onSetInterest(f.id,patch);},[onSetInterest,f.id]);
  return(<div data-callback-card={f.kind+"-"+f.id} onClick={function(){onOpen(f);}} title={isFile?"Open the file":"Work it in the New Call console"} style={{padding:"11px 20px",borderBottom:!isLast?"1px solid "+C.border:"none",cursor:"pointer",background:ist.booked?C.booked:C.orange+"08",boxShadow:ist.booked?"inset 4px 0 0 "+C.bookedEdge:"none"}}>
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
      <div style={{display:"flex",alignItems:"center",gap:14,flexWrap:"wrap",minWidth:0}}>
        <span style={{fontSize:11,color:col}} aria-hidden="true">{"☎"}</span>
        {showOwner&&<OwnerChip owner={f.owner}/>}
        <span style={{fontSize:12,fontFamily:"'Mulish',system-ui,sans-serif",color:C.accentLight,minWidth:65}}>{isFile?f.id:"Lead"}</span>
        <span style={{display:"inline-flex",alignItems:"center",gap:7}}>
          {isFile&&onSetInterest&&<InterestMenu loan={raw} onSet={setInterest} onOpenChange={function(v){if(onMenuLock)onMenuLock(f.id,v);}}/>}
          <span data-interest-name={isFile?f.id:undefined} title={ist.label||undefined} style={Object.assign({fontSize:13,fontWeight:ist.level?800:600},isFile?interestNameStyle(raw):{})}>{f.name}</span>
        </span>
        <Badge label={"Callback due "+fmtDob(f.date)} color={col} bg={col+"18"}/>
        <span style={{fontSize:11,color:C.textDim}}>{where}</span>
        {f.optedOut&&<span title="Off mailings and the Outbound dialer — calling them back is fine" style={{fontSize:10.5,color:C.textDim}}>Opted out · off mailings</span>}
      </div>
      <div style={{display:"flex",alignItems:"center",gap:10}}>
        {f.phone&&<span onClick={function(e){e.stopPropagation();}}><CallBtn phone={f.phone} small/></span>}
        <span style={{fontSize:11,fontWeight:700,color:C.accentLight,whiteSpace:"nowrap"}}>{isFile?"Open file ›":"Work it ›"}</span>
      </div>
    </div>
    {f.note&&<div style={{marginTop:5,marginLeft:26,fontSize:12,color:C.textMuted,fontStyle:"italic"}}>{f.note}</div>}
  </div>);
});

// One stage lane. Memoized: when hover/drag state changes, only the lane whose
// props actually changed (the one under the cursor) re-renders.
// `items` is what the lane shows, in order: {kind:"file",l,due} (a pipeline
// file; `due` = a due call back badge) and, in the Prospect lane only,
// {kind:"cb",f} (a due call back card). `sl` = the files alone (the comp
// total), `total` = everything before the search filter.
// hasSearch: a search OR an interest filter is narrowing the lane (hiddenBy
// says which, for the "N hidden by …" chip).
const StageLane = React.memo(function StageLane({st,items,sl,total,today,isOver,hasSearch,hiddenBy,draggedId,hoveredId,onDragOverStage,onDragLeave,onDropStage,onDragStart,onDragEnd,onOpen,onOpenCallback,onHover,onUnhover,onNote,onSetInterest,onMenuLock,showOwner}){
  const c=STC[st];
  const hiddenCount=total-items.length;
  const cbCount=items.reduce(function(a,it){return a+(it.kind==="cb"?1:0);},0);
  return(<div onDragOver={function(e){onDragOverStage(e,st);}} onDragLeave={onDragLeave} onDrop={function(e){onDropStage(e,st);}} style={{background:C.card,borderRadius:12,border:"1px solid "+(isOver?c:C.border),overflow:"hidden",transition:"border-color 0.15s",boxShadow:isOver?"0 0 12px "+c+"30":"none",opacity:hasSearch&&total>0&&items.length===0?.35:1}}>
    <div style={{padding:"12px 20px",borderBottom:items.length?"1px solid "+C.border:"none",display:"flex",alignItems:"center",justifyContent:"space-between",background:c+"06",gap:10,flexWrap:"wrap"}}>
      <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
        <div style={{width:10,height:10,borderRadius:"50%",background:c}}/>
        <span style={{fontSize:14,fontWeight:700}}>{st}</span>
        {cbCount>0&&<span title="Due call backs — fallout files and mail-list leads. They show here until they're worked; nothing about them changes." style={{fontSize:10.5,fontWeight:700,color:C.orange,background:C.orangeBg,padding:"1px 8px",borderRadius:10}}>incl. {cbCount} call back{cbCount===1?"":"s"} due</span>}
        {hiddenCount>0&&<span style={{fontSize:10,color:C.textDim,background:C.surfaceAlt,padding:"1px 7px",borderRadius:10}}>{hiddenCount} hidden by {hiddenBy||"search"}</span>}
      </div>
      <div style={{display:"flex",alignItems:"center",gap:10}}>
        <span data-lane-count={st} style={{fontSize:12,fontWeight:700,color:c,background:c+"18",padding:"2px 10px",borderRadius:10}}>{items.length}{total!==items.length?" / "+total:""}</span>
        <span style={{fontSize:12,color:C.gold,fontWeight:600,fontFamily:"'Mulish',system-ui,sans-serif"}}>${sl.reduce(function(a,l){return a+(l.comm||0);},0).toLocaleString()}</span>
      </div>
    </div>
    {items.length===0
      ?<div style={{padding:isOver?20:14,textAlign:"center",fontSize:12,color:isOver?c:C.textDim,fontWeight:isOver?600:400,transition:"all 0.15s"}}>
        {isOver?"Drop here to move to "+st:hasSearch&&total>0?"No matches in this stage":"Empty"}
      </div>
      :items.map(function(it,i){
        var last=i===items.length-1;
        if(it.kind==="cb")return(<CallbackRow key={"cb-"+it.f.kind+"-"+it.f.id} f={it.f} isLast={last} overdue={it.f.date<today} onOpen={onOpenCallback} showOwner={showOwner} onSetInterest={onSetInterest} onMenuLock={onMenuLock}/>);
        var l=it.l;
        return(<LoanRow key={l.id} l={l} isLast={last} isDragged={draggedId===l.id} isHovered={hoveredId===l.id} onDragStart={onDragStart} onDragEnd={onDragEnd} onOpen={onOpen} onHover={onHover} onUnhover={onUnhover} onNote={onNote} onSetInterest={onSetInterest} onMenuLock={onMenuLock} showOwner={showOwner}
          callbackDue={it.due||null} callbackOverdue={!!it.due&&it.due<today}/>);
      })}
    {isOver&&items.length>0&&<div style={{padding:"8px 20px",textAlign:"center",fontSize:11,color:c,fontWeight:600,borderTop:"1px dashed "+c+"40"}}>Drop to move here</div>}
  </div>);
});

function Pipeline({loans:allLoans,setLoans,go,goCalc,sendSMS,onOpenAutomations,onStartCall,mktgLeads:allLeads,rateSheets,staff}){
  const me=STAFF[staff]?staff:"reece";
  const meRef=useRef(me);meRef.current=me;
  // Max: his own files only (access.ownFilesOnly). App already hands him only
  // his; filtering again here means nothing else can ever show on his board,
  // and no mail-list lead ever reaches it (call backs come from his files).
  const ownOnly=!!staffAccess(me).ownFilesOnly;
  const loans=useMemo(function(){return ownOnly?(allLoans||[]).filter(function(l){return l&&ownerOf(l)===me;}):allLoans;},[allLoans,ownOnly,me]);
  const mktgLeads=ownOnly?null:allLeads;
  // Practice mode (Max): what he adds shows here but is never saved — the
  // confirmation says so.
  const practice=usePractice(me);
  const [boardState,setBoard]=useState("pipeline"); // "pipeline" | "fallout" | "reprice"
  // No Re-price Radar on Max's board.
  const board=ownOnly&&boardState==="reprice"?"pipeline":boardState;
  const [dragId,sDragId]=useState(null);
  const [dragOver,sDragOver]=useState(null);
  const [search,setSearch]=useState("");
  // Sort — "Interest (least → most)" unless he picked another (SORT_KEY).
  const [sortBy,setSortByState]=useState(readSort);
  const setSortBy=useCallback(function(v){setSortByState(v);writeSort(v);},[]);
  const [showArchived,setShowArchived]=useState(false); // item #16
  // Hover preview — {id, rect, px} (the row's rect + the pointer's x when it
  // opened) for the fixed-position preview card (HoverPreview). Fixed
  // positioning (not absolute) because the stage lanes clip overflow. A
  // scroll / resize closes it (it is fixed to where the row WAS).
  const [hover,sHover]=useState(null);
  useInputModality();
  useEffect(function(){
    if(!hover)return undefined;
    var close=function(){sHover(null);};
    window.addEventListener("scroll",close,true);
    window.addEventListener("resize",close);
    return function(){window.removeEventListener("scroll",close,true);window.removeEventListener("resize",close);};
  },[hover]);
  // Whose files (see OWNER_VIEW_KEY). Default "mine". Max's board is always
  // his own — no toggle, and his choice is never remembered over Reece's.
  // With the owner UI off (src/features.js) every board is "all": no file is
  // ever hidden because of its owner, and the stored pick isn't touched.
  const ownershipUi=useOwnershipUi();
  const [ownerViewState,setOwnerViewState]=useState(readOwnerView);
  const ownerView=ownOnly?"mine":ownershipUi?ownerViewState:"all";
  const setOwnerView=useCallback(function(v){setOwnerViewState(v);writeOwnerView(v);},[]);
  const inView=useCallback(function(l){return ownerInView(ownerOf(l),ownerView,me);},[ownerView,me]);
  // Interest filter (src/interest.js) — remembered per browser.
  const [ifilter,setIfilterState]=useState(readInterestFilter);
  const setIfilter=useCallback(function(f){setIfilterState(f);writeInterestFilter(f);},[]);
  const toggleIfilter=useCallback(function(key){setIfilterState(function(prev){var n=toggleInterestFilter(prev,key);writeInterestFilter(n);return n;});},[]);
  const clearIfilter=useCallback(function(){setIfilter({levels:[],booked:false});},[setIfilter]);
  const filterOn=interestFilterActive(ifilter);
  // The dot on a row / call back card / Fallout row: one file's level or
  // Booked switch, through the normal loans save (nothing else is written).
  const onSetInterest=useCallback(function(id,patch){setLoans(function(prev){return applyInterest(prev,id,patch);});},[setLoans]);
  // Fallout: its own filter, always starting on All (never the pipeline's
  // remembered "Mine"), so both users' fallout files and call-backs show.
  const [falloutViewState,setFalloutView]=useState("all");
  const falloutView=ownOnly?"mine":ownershipUi?falloutViewState:"all";
  const falloutInView=useCallback(function(l){return ownerInView(ownerOf(l),falloutView,me);},[falloutView,me]);
  // Owner badges on every row whenever more than one person's files can show
  // (and the owner UI is on).
  const showOwner=!ownOnly&&ownershipUi&&ownerView==="all";
  // The radar computes its own rows; it reports the per-owner counts back.
  const [radarCounts,setRadarCounts]=useState(null);
  // "Add a deal" — a deal priced outside the calculator.
  const [addDeal,setAddDeal]=useState(false);
  const [dealAdded,setDealAdded]=useState(null); // the file just added, for the confirmation strip
  // "+ New lead" — Max's way in for a new person (he has no New Call).
  const [addLead,setAddLead]=useState(false);
  const [leadAdded,setLeadAdded]=useState(null);

  // item #16 — active loans excludes archived unless toggled.
  // Fallout files carry archived:true but live on their own board, so they are
  // excluded from the stage lanes even when "Show Archived" is on — otherwise
  // toggling it would silently drop them (their stage isn't in STAGES, so no
  // lane would ever claim them) and the count would lie.
  const activePool=useMemo(function(){
    return loans.filter(function(l){
      if(isFallout(l))return false;
      return showArchived||!l.archived;
    });
  },[loans,showArchived]);
  const falloutPool=useMemo(function(){return loans.filter(isFallout);},[loans]);
  const visibleLoans=useMemo(function(){return activePool.filter(inView);},[activePool,inView]);
  const queueLoans=useMemo(function(){return loans.filter(inView);},[loans,inView]);
  const archivedCount=useMemo(function(){return loans.filter(function(l){return l.archived&&!isFallout(l)&&inView(l);}).length;},[loans,inView]);
  // Counts and the "due for a call back" banner cover BOTH users' fallout.
  const falloutCounts=useMemo(function(){
    const all=falloutPool;
    return{
      qual:all.filter(function(l){return l.stage===FALLOUT_QUAL;}).length,
      denied:all.filter(function(l){return l.stage===FALLOUT_DENIED;}).length,
      noapp:all.filter(function(l){return l.stage===FALLOUT_NOAPP;}).length,
      due:all.filter(function(l){return isDueBy(l.followUpDate);}).length,
    };
  },[falloutPool]);
  const ownerCounts=useMemo(function(){
    if(board==="reprice")return radarCounts;
    return countOwners(board==="fallout"?falloutPool:activePool,me);
  },[board,radarCounts,falloutPool,activePool,me]);

  // ── Due call backs → the Prospect lane (display only) ──
  // Reece, 10/1: due call backs "automatically show as prospect". Files that
  // already have a card on the board (live, or archived while "Show Archived"
  // is on) keep it and get a badge; fallout / archived files and mail-list
  // leads get a card in Prospect. Owner view applies to both.
  const today=isoDatePlus(0);
  const callbackLane=useMemo(function(){
    return prospectLaneCallbacks(loans,mktgLeads,today,function(l){return !isFallout(l)&&(showArchived||!l.archived);});
  },[loans,mktgLeads,today,showArchived]);
  const callbackCards=useMemo(function(){
    return callbackLane.cards.filter(function(c){return ownerInView(c.owner,ownerView,me);});
  },[callbackLane,ownerView,me]);
  const goRef=useRef(go);goRef.current=go;
  const startCallRef=useRef(onStartCall);startCallRef.current=onStartCall;
  const onOpenCallback=useCallback(function(f){
    if(f.kind==="loan"){if(goRef.current)goRef.current(f.raw);return;}
    if(startCallRef.current)startCallRef.current({prospect:prospectFromLead(f.raw)});
  },[]);

  // dragId mirrored in a ref so the drop handler stays referentially stable
  const dragIdRef=useRef(null);
  const onDragStart=useCallback(function(e,loanId){dragIdRef.current=loanId;sDragId(loanId);sHover(null);e.dataTransfer.effectAllowed="move";},[]);
  const onDragEnd=useCallback(function(){dragIdRef.current=null;sDragId(null);sDragOver(null);},[]);
  const onDragOverStage=useCallback(function(e,stage){e.preventDefault();sDragOver(stage);},[]);
  const onDragLeave=useCallback(function(){sDragOver(null);},[]);
  const onDropStage=useCallback(function(e,newStage){
    e.preventDefault();sDragOver(null);
    const id=dragIdRef.current;
    if(!id)return;
    setLoans(function(prev){return prev.map(function(l){
      if(l.id!==id||l.stage===newStage)return l;
      var ts=new Date();var logEntry={text:"Stage changed: "+l.stage+" → "+newStage+" (drag & drop)",type:"stage",timestamp:ts.toLocaleString(),date:ts.toLocaleDateString(),time:ts.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})};
      return Object.assign({},l,{stage:newStage,activityLog:(l.activityLog||[]).concat([logEntry])});
    });});
    dragIdRef.current=null;sDragId(null);
  },[setLoans]);
  const onHover=useCallback(function(h){sHover(h);},[]);
  // A row closes only its OWN preview (id); no id = whatever is open.
  const onUnhover=useCallback(function(id){sHover(function(h){return !h||(id&&h.id!==id)?h:null;});},[]);
  // Board comment ("where it stands"). Also logs to the file's activity so the
  // history of what was said about it is readable from Loan Detail.
  // opts.log=false → the autosave while typing: store the text, no log line.
  // opts.log (default) → a commit: one log line when the text differs from
  // opts.from (what it was when editing began) — never one per keystroke.
  // A logged comment claims an unowned file for whoever wrote it.
  const onNote=useCallback(function(id,text,opts){
    var o=opts||{log:true};
    setLoans(function(prev){return prev.map(function(l){
      if(l.id!==id)return l;
      var t=text||"";
      var changed=(l.statusNote||"")!==t;
      var logIt=!!o.log&&(o.from==null?changed:(o.from||"")!==t);
      if(!changed&&!logIt)return l;
      var ts=new Date();
      var n=Object.assign({},l);
      if(changed){n.statusNote=t||null;n.statusNoteAt=t?ts.toISOString():null;}
      if(logIt){
        n.activityLog=(l.activityLog||[]).concat([{text:t?"Board comment: "+t:"Board comment cleared",type:"note",timestamp:ts.toLocaleString(),date:ts.toLocaleDateString(),time:ts.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}),epoch:ts.getTime()}]);
        n=claimLoan(n,meRef.current);
      }
      return n;
    });});
  },[setLoans]);

  // Search filter — applied within each stage lane
  const q=search.toLowerCase().trim();

  // Per-stage buckets, filtered + sorted. Recomputed only when the data or the
  // controls change — hover/drag state doesn't invalidate these, so memoized
  // lanes skip re-rendering entirely.
  const buckets=useMemo(function(){
    // The interest filter narrows the lanes like the search does.
    const matchesSearch=function(l){
      if(!interestFilterMatch(l,ifilter))return false;
      if(!q)return true;
      return l.b.toLowerCase().includes(q)||l.id.toLowerCase().includes(q)||l.prop.toLowerCase().includes(q)||(l.ph||"").includes(q)||(l.em||"").toLowerCase().includes(q);
    };
    const parseDate=function(s){if(!s||s==="TBD")return null;var p=s.split("/");if(p.length===3)return new Date(p[2],p[0]-1,p[1]);return null;};
    const sortLoans=function(arr){
      if(sortBy==="default")return arr;
      if(sortBy==="interest")return sortByInterest(arr);
      var a=[...arr];
      if(sortBy==="close_asc")a.sort(function(x,y){var dx=parseDate(x.estClose),dy=parseDate(y.estClose);if(!dx&&!dy)return 0;if(!dx)return 1;if(!dy)return -1;return dx-dy;});
      if(sortBy==="close_desc")a.sort(function(x,y){var dx=parseDate(x.estClose),dy=parseDate(y.estClose);if(!dx&&!dy)return 0;if(!dx)return 1;if(!dy)return -1;return dy-dx;});
      if(sortBy==="comm_desc")a.sort(function(x,y){return y.comm-x.comm;});
      if(sortBy==="name_asc")a.sort(function(x,y){return x.b.localeCompare(y.b);});
      return a;
    };
    const matchesCallback=function(f){
      // A mail-list lead has no interest level: an interest filter hides it.
      if(filterOn&&(f.kind!=="loan"||!interestFilterMatch(f.raw,ifilter)))return false;
      if(!q)return true;
      var raw=f.raw||{};
      return String(f.name||"").toLowerCase().includes(q)||String(f.id||"").toLowerCase().includes(q)||String(f.phone||"").includes(q)
        ||String(f.note||"").toLowerCase().includes(q)||String(raw.prop||raw.address||"").toLowerCase().includes(q)||String(raw.em||raw.email||"").toLowerCase().includes(q);
    };
    const byDue=function(a,b){return a.due<b.due?-1:a.due>b.due?1:0;};
    const badges=callbackLane.badges;
    const toItem=function(l){return {kind:"file",l:l,due:badges[l.id]||null};};
    // The file an item is ranked by: a file row its file, a call back card
    // its file (a mail-list lead has none → Not set).
    const itemLoan=function(it){return it.kind==="file"?it.l:(it.f&&it.f.kind==="loan"?it.f.raw:null);};
    return STAGES.map(function(st){
      var allInStage=visibleLoans.filter(function(l){return l.stage===st;});
      var matched=allInStage.filter(matchesSearch);
      var sl=sortLoans(matched);
      var items=sl.map(toItem);
      var total=allInStage.length;
      if(st==="Prospect"){
        total+=callbackCards.length;
        var cbs=callbackCards.filter(matchesCallback).map(function(f){return {kind:"cb",f:f,due:f.date};});
        // Original order: everything with a call back due first (oldest date
        // first), then the rest of the lane as it was. Interest (the default)
        // ranks THAT order least → most interested, call back cards included
        // (ties keep it). An explicit sort keeps the files in that order and
        // lists the call backs after them.
        if(sortBy==="default"||sortBy==="interest"){
          var base=sortBy==="interest"?matched.map(toItem):items;
          items=base.filter(function(it){return it.due;}).concat(cbs).sort(byDue).concat(base.filter(function(it){return !it.due;}));
          if(sortBy==="interest")items=sortByInterest(items,itemLoan);
        }
        else items=items.concat(cbs);
      }
      return {st:st,allInStage:allInStage,sl:sl,items:items,total:total};
    });
  },[visibleLoans,q,sortBy,callbackLane,callbackCards,ifilter,filterOn]);

  // While a row's interest menu is open its lane keeps the order it had
  // (menuLock = that file's id). The menu is fixed where the dot was, and
  // the Booked switch leaves it open — under the Interest sort, booking a
  // file re-ranks it, so the row slid away and the menu sat over the row
  // that took its place (the next click edited a file he wasn't looking
  // at). The lane re-sorts the moment the menu closes. Rows new to the lane
  // meanwhile go after the held ones, in their sorted order.
  const [menuLock,setMenuLock]=useState(null);
  const onMenuLock=useCallback(function(id,open){setMenuLock(function(cur){return open?id:(cur===id?null:cur);});},[]);
  const laneOrderRef=useRef({});
  const lanes=useMemo(function(){
    var itemKey=function(it){return it.kind==="file"?"f:"+it.l.id:"cb:"+it.f.kind+"-"+it.f.id;};
    var itemLoanId=function(it){return it.kind==="file"?it.l.id:(it.f&&it.f.kind==="loan"?it.f.id:null);};
    var prev=laneOrderRef.current;var next={};
    var out=buckets.map(function(b){
      var items=b.items;
      var held=prev[b.st];
      if(menuLock&&held&&items.some(function(it){return itemLoanId(it)===menuLock;})){
        var pos=Object.create(null);held.forEach(function(k,i){pos[k]=i;});
        var kept=items.filter(function(it){return pos[itemKey(it)]!=null;}).sort(function(a,c){return pos[itemKey(a)]-pos[itemKey(c)];});
        var fresh=items.filter(function(it){return pos[itemKey(it)]==null;});
        var frozen=kept.concat(fresh);
        if(frozen.some(function(it,i){return it!==items[i];}))b=Object.assign({},b,{items:frozen});
      }
      next[b.st]=b.items.map(itemKey);
      return b;
    });
    laneOrderRef.current=next;
    return out;
  },[buckets,menuLock]);

  const totalMatches=useMemo(function(){
    if(!search&&!filterOn)return null;
    return buckets.reduce(function(a,b){return a+b.items.length;},0);
  },[search,filterOn,buckets]);
  // "See what's going on": every FILE the board shows — the lanes' files
  // (whose files + Show Archived) AND the fallout / archived files whose call
  // back card sits in the Prospect lane (they carry the dot and the filter
  // narrows them too, so they count) — each level's files and est. comp. A
  // mail-list lead's card is not a file. The filter itself never moves these.
  const boardFiles=useMemo(function(){
    var seen=Object.create(null);var out=[];
    var add=function(l){if(l&&l.id&&!seen[l.id]){seen[l.id]=1;out.push(l);}};
    visibleLoans.forEach(add);
    callbackCards.forEach(function(f){if(f.kind==="loan")add(f.raw);});
    return out;
  },[visibleLoans,callbackCards]);
  const interestCounts=useMemo(function(){return interestSummary(boardFiles);},[boardFiles]);
  // "Showing N of M files": N = the files the lanes actually show, the search
  // included (with a filter on it agrees with the toolbar's "N matches" — a
  // lead's card hides under any filter); M = every file on the board.
  const interestShown=useMemo(function(){
    return buckets.reduce(function(a,b){return a+b.items.reduce(function(n,it){return n+((it.kind==="file"||(it.f&&it.f.kind==="loan"))?1:0);},0);},0);
  },[buckets]);
  const hiddenBy=search&&filterOn?"search & filter":filterOn?"filter":"search";

  const labelFor=function(v){return v==="all"?"everyone's":v==="mine"?"your":STAFF[otherStaff(me)].name+"'s";};
  const ownerLabel=labelFor(board==="fallout"?falloutView:ownerView);
  // "showing everyone's / your / Max's files" — only with the owner UI on.
  const ownerWords=ownOnly||ownershipUi;
  return(<div>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,marginBottom:16,flexWrap:"wrap"}}>
      <Hdr t={board==="reprice"?"Re-price Radar":board==="fallout"?"Fallout":"Pipeline"}
        s={board==="reprice"?"Everyone whose numbers got better because rates moved"+(ownerWords?" — showing "+ownerLabel+" files":"")
          :board==="fallout"?"Every call worked that didn't get pitched — the reason kept, and the numbers where there were any."+(ownerWords?" Showing "+ownerLabel+" files.":"")
          :ownOnly?"Your files — add a lead, drag files between stages, open one to track it"
          :"Drag and drop files between stages"+(ownerWords?" — showing "+ownerLabel+" files":"")}/>
      <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
        {/* One click: every stage lane shows only booked files — the SAME
            filter.booked as the Booked chip (10/10/26). */}
        {board==="pipeline"&&<BookedLeadsToggle count={interestCounts.booked.n} on={ifilter.booked} onToggle={function(){toggleIfilter("booked");}}/>}
        {!ownOnly&&ownershipUi&&<OwnerToggle me={me} view={board==="fallout"?falloutView:ownerView} setView={board==="fallout"?setFalloutView:setOwnerView} counts={ownerCounts}/>}
        {ownOnly&&board==="pipeline"&&<Btn small primary onClick={function(){setLeadAdded(null);setDealAdded(null);setAddLead(true);}}>+ New lead</Btn>}
        {(me==="reece"||ownOnly)&&board==="pipeline"&&<Btn small primary={!ownOnly} onClick={function(){setLeadAdded(null);setDealAdded(null);setAddDeal(true);}}>+ Add a deal</Btn>}
        {onOpenAutomations&&board==="pipeline"&&<Btn small onClick={onOpenAutomations} style={{color:C.green,border:"1px solid "+C.green+"40",background:C.green+"12"}}><span style={{color:C.green,display:"inline-flex"}}>{Ic.auto}</span>Automations</Btn>}
      </div>
    </div>

    {/* ── Board switch — active pipeline vs the fallout list ── */}
    <div style={{display:"flex",gap:8,marginBottom:18,flexWrap:"wrap",alignItems:"center"}}>
      {[{id:"pipeline",l:"Active pipeline",n:visibleLoans.length},
        {id:"fallout",l:"Fallout",n:falloutCounts.qual+falloutCounts.denied+falloutCounts.noapp}]
        .concat(ownOnly?[]:[{id:"reprice",l:"Re-price Radar",n:null}]).map(function(o){
        const on=board===o.id;
        return <button key={o.id} onClick={function(){setBoard(o.id);}} style={{padding:"9px 18px",borderRadius:9,cursor:"pointer",fontSize:13,fontWeight:800,fontFamily:"'Mulish',system-ui,sans-serif",
          border:"1px solid "+(on?C.accent:C.border),background:on?C.accentGlow:"transparent",color:on?C.accentLight:C.textMuted}}>
          {o.l}{o.n!=null&&<span style={{opacity:.7}}> ({o.n})</span>}
        </button>;
      })}
      {falloutCounts.due>0&&<button onClick={function(){setBoard("fallout");}} style={{padding:"7px 14px",borderRadius:20,cursor:"pointer",fontSize:11.5,fontWeight:800,fontFamily:"'Mulish',system-ui,sans-serif",
        border:"1px solid "+C.orange+"60",background:C.orangeBg,color:C.orange}}>
        {falloutCounts.due} fallout {falloutCounts.due===1?"file is":"files are"} due for a call back
      </button>}
    </div>

    {/* ── Confirmation after "+ New lead" ── */}
    {leadAdded&&<div role="status" style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",padding:"10px 16px",marginBottom:16,borderRadius:10,border:"1px solid "+C.green+"50",background:C.greenBg,fontSize:12.5,color:C.text}}>
      <span style={{fontWeight:800,color:C.green}}>✓ Added {leadAdded.id}</span>
      <span>{leadAdded.b} · Prospect — on your “Spoken to — numbers not run” list.</span>
      {practice&&<span style={{fontWeight:800,color:C.orange}}>Practice mode — on this screen only, not saved.</span>}
      <span style={{flex:1}}/>
      {go&&<Btn small onClick={function(){var l=leadAdded;setLeadAdded(null);go(l);}}>Open file</Btn>}
      {goCalc&&<Btn small primary onClick={function(){var l=leadAdded;setLeadAdded(null);goCalc(l);}}>{Ic.calc} Run numbers</Btn>}
      <button onClick={function(){setLeadAdded(null);}} aria-label="Dismiss" style={{border:"none",background:"none",color:C.textDim,cursor:"pointer",fontSize:16,lineHeight:1}}>×</button>
    </div>}

    {/* ── Confirmation after "Add a deal" ── */}
    {dealAdded&&<div role="status" style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",padding:"10px 16px",marginBottom:16,borderRadius:10,border:"1px solid "+C.green+"50",background:C.greenBg,fontSize:12.5,color:C.text}}>
      <span style={{fontWeight:800,color:C.green}}>✓ Added {dealAdded.id}</span>
      <span>{dealAdded.b} · {dealAdded.stage}{dealAdded.comm?" · comp $"+dealAdded.comm.toLocaleString():""} — marked “{MANUAL_DEAL_LABEL}”.</span>
      {practice&&<span style={{fontWeight:800,color:C.orange}}>Practice mode — on this screen only, not saved.</span>}
      <span style={{flex:1}}/>
      {go&&<Btn small onClick={function(){var l=dealAdded;setDealAdded(null);go(l);}}>Open file</Btn>}
      <button onClick={function(){setDealAdded(null);}} aria-label="Dismiss" style={{border:"none",background:"none",color:C.textDim,cursor:"pointer",fontSize:16,lineHeight:1}}>×</button>
    </div>}

    {board==="reprice"?<RepriceRadar rateSheets={rateSheets} onStartCall={onStartCall} loans={loans} mktgLeads={mktgLeads} staff={me} ownerView={ownerView} onOwnerCounts={setRadarCounts}/>
    :board==="fallout"?<FalloutBoard loans={loans} setLoans={setLoans} go={go} onStartCall={onStartCall} rateSheets={rateSheets} inView={ownOnly||falloutView==="all"?null:falloutInView} staff={me} ownerLabel={ownerLabel} showOwner={!ownOnly&&ownershipUi} onShowAll={!ownOnly&&falloutView!=="all"?function(){setFalloutView("all");}:null}/>:<>

    {/* ── Search + Sort + Archive toolbar ── */}
    <div style={{display:"flex",gap:10,marginBottom:16,flexWrap:"wrap",alignItems:"center"}}>
      <div style={{flex:"1 1 260px",position:"relative"}}>
        <span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",color:C.textDim,pointerEvents:"none"}}>{Ic.search}</span>
        <input
          type="text" placeholder="Search by name, ID, address, phone, email..."
          value={search} onChange={e=>setSearch(e.target.value)}
          style={{width:"100%",padding:"10px 14px 10px 36px",borderRadius:8,border:"1px solid "+(search?C.accent:C.border),background:C.surfaceAlt,color:C.text,fontSize:13,outline:"none",fontFamily:"'Mulish',system-ui,sans-serif",boxSizing:"border-box"}}
        />
        {search&&<button onClick={()=>setSearch("")} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:C.textDim,cursor:"pointer",fontSize:16,lineHeight:1}}>×</button>}
      </div>
      <div role="group" aria-label="Sort" style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
        <span style={{fontSize:11,color:C.textDim,fontWeight:600}}>SORT:</span>
        {SORT_OPTIONS.map(opt=>(
          <button key={opt.v} data-sort={opt.v} aria-pressed={sortBy===opt.v} title={opt.t} onClick={()=>setSortBy(opt.v)} style={{padding:"6px 12px",borderRadius:6,border:"1px solid "+(sortBy===opt.v?C.accent:C.border),background:sortBy===opt.v?C.accentGlow:"transparent",color:sortBy===opt.v?C.accentLight:C.textMuted,fontSize:11,fontWeight:sortBy===opt.v?700:500,cursor:"pointer",fontFamily:"'Mulish',system-ui,sans-serif",whiteSpace:"nowrap"}}>{opt.l}</button>
        ))}
      </div>
      {/* item #16 — archive toggle */}
      {archivedCount>0&&<button onClick={()=>setShowArchived(function(p){return !p;})} style={{padding:"6px 14px",borderRadius:6,border:"1px solid "+(showArchived?C.cyan:C.border),background:showArchived?C.cyan+"18":"transparent",color:showArchived?C.cyan:C.textMuted,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Mulish',system-ui,sans-serif",whiteSpace:"nowrap"}}>
        🗂 {showArchived?"Hide":"Show"} Archived ({archivedCount})
      </button>}
      {totalMatches!==null&&<span style={{fontSize:12,color:C.accentLight,fontWeight:600}}>{totalMatches} match{totalMatches!==1?"es":""}</span>}
    </div>

    {/* ── Booked leads only (the header switch / the Booked chip) ── */}
    {ifilter.booked&&<BookedStrip filter={ifilter} shown={interestShown} total={boardFiles.length} onShowAll={clearIfilter}/>}

    {/* ── Interest levels — how many files at each level (and booked), with
        their est. comp; clicking narrows the stage lanes. ── */}
    <InterestBar summary={interestCounts} filter={ifilter} onToggle={toggleIfilter} onClear={clearIfilter} shown={interestShown} total={boardFiles.length}/>

    {/* ── Working lists — call-ins + numbers to run, above the stage lanes.
        Nothing at all when both are empty; no quick call-in form (10/2 — a
        caller is taken through New Call). ── */}
    <WorkQueues loans={queueLoans} setLoans={setLoans} go={go} goCalc={goCalc} onStartCall={onStartCall} staff={me} showOwner={showOwner}/>

    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      {lanes.map(function(b){
        var laneHasDragged=dragId&&b.sl.some(function(l){return l.id===dragId;});
        var laneHasHovered=hover&&b.sl.some(function(l){return l.id===hover.id;});
        return(<StageLane key={b.st} st={b.st} items={b.items} sl={b.sl} total={b.total} today={today} onNote={onNote} onSetInterest={onSetInterest} onMenuLock={onMenuLock} showOwner={showOwner}
          isOver={dragOver===b.st} hasSearch={!!search||filterOn} hiddenBy={hiddenBy}
          draggedId={laneHasDragged?dragId:null} hoveredId={laneHasHovered?hover.id:null}
          onDragOverStage={onDragOverStage} onDragLeave={onDragLeave} onDropStage={onDropStage}
          onDragStart={onDragStart} onDragEnd={onDragEnd} onOpen={go} onOpenCallback={onOpenCallback} onHover={onHover} onUnhover={onUnhover}/>);
      })}
    </div>

    {/* ── Hover preview — key deal info + Top To-Dos without opening the file ── */}
    {hover&&!dragId&&(function(){
      var hl=loans.find(function(l){return l.id===hover.id;});
      if(!hl)return null;
      var todos=topTodos(hl);
      var a=loanAge(hl);
      var sc=STC[hl.stage]||C.accent;
      var hst=interestStyle(hl);
      return(<HoverPreview key={hl.id} id={hl.id} anchor={hover} edge={sc}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,marginBottom:8}}>
          <div style={Object.assign({fontSize:14,fontWeight:800,color:C.text,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"},interestNameStyle(hl),hst.booked?{background:C.booked,padding:"0 6px",borderRadius:5}:null)}>{hl.b}</div>
          <Badge label={hl.stage} color={sc} bg={sc+"18"}/>
        </div>
        <div style={{marginBottom:10}}>
          {(ownershipUi?[["Owner",STAFF[ownerOf(hl)]?STAFF[ownerOf(hl)].name:ownerOf(hl)]]:[]).concat([["Phone",hl.ph||"—"],["Email",hl.em||"—"],["DOB",hl.dob?fmtDob(hl.dob)+(a?"  ·  age "+a:""):(a?"age "+a+" (no DOB on file)":"—")],["Home Value",hl.hv?"$"+hl.hv.toLocaleString():"—"],[isHs2File(hl)?"First lien (stays)":"Balance",hl.mb?"$"+hl.mb.toLocaleString():"—"],["Your Comp",hl.comm?"$"+hl.comm.toLocaleString():"—"],["Est. Close",hl.estClose||"—"],["Interest",hst.label||"Not set"]]).concat(stageAtOrPast(hl,LOCK_STAGE)?[["Counseling",(hl.counselingDate||(hl.counseling&&hl.counseling.date))?fmtDob(hl.counselingDate||hl.counseling.date)+(hl.counselorSent?"  ·  pkg sent ✓":"  ·  pkg NOT sent"):"date needed"]]:[]).map(function(r){return(<div key={r[0]} style={{display:"flex",justifyContent:"space-between",gap:10,fontSize:11,padding:"2.5px 0",borderBottom:"1px solid "+C.border}}><span style={{color:C.textDim,flexShrink:0}}>{r[0]}</span><span style={{color:C.text,fontWeight:600,textAlign:"right",minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r[1]}</span></div>);})}
        </div>
        <div style={{fontSize:10,fontWeight:800,color:C.textDim,letterSpacing:.6,marginBottom:6}}>TOP TO-DOS</div>
        {todos.length===0?<div style={{fontSize:11,color:C.green,fontWeight:600}}>✓ Nothing outstanding</div>
        :todos.slice(0,6).map(function(t,i){return(<div key={i} style={{display:"flex",gap:7,alignItems:"flex-start",padding:"3px 0"}}>
          <span style={{width:7,height:7,borderRadius:"50%",marginTop:4,flexShrink:0,background:t.urgent?C.red:C.orange}}/>
          <span style={{fontSize:11.5,color:t.urgent?C.red:C.text,fontWeight:t.urgent?700:500,lineHeight:1.35}}>{t.t}</span>
        </div>);})}
        {todos.length>6&&<div style={{fontSize:10,color:C.textDim,marginTop:4}}>+{todos.length-6} more — click to open the file</div>}
      </HoverPreview>);
    })()}
    </>}

    {addDeal&&<AddDealModal loans={loans} setLoans={setLoans} staff={me}
      onClose={function(){setAddDeal(false);}}
      onSaved={function(loan){setAddDeal(false);setDealAdded(loan);}}/>}
    {addLead&&<NewLeadModal loans={loans} setLoans={setLoans} staff={me}
      onClose={function(){setAddLead(false);}}
      onSaved={function(loan){setAddLead(false);setLeadAdded(loan);}}/>}
  </div>);
}

// ═══════════════════════════════════════════════════════════════
// + NEW LEAD — a person typed straight onto the pipeline (Max's board)
// ═══════════════════════════════════════════════════════════════
// Max has no New Call console and no mail list. Reece, 10/2: "if practice
// mode is on, nothing saves, if it's off, then he can enter the stuff in for
// the lead and track stuff". So this takes what he has — who, how to reach
// them, the property, the youngest borrower's DOB, value, balance and the
// HECM / FTR / HS2 deal type (the console's lead-card pick) — and makes a
// Prospect file he owns (lib.js newLeadFile: LN-M###, on his "numbers not
// run" list). Only the name is required. In Practice mode the file still
// appears (it is his session to play in) — App never saves it.
const NEW_LEAD_BLANK={name:"",phone:"",email:"",street:"",city:"",state:"",zip:"",dob:"",homeValue:"",balance:"",dealType:"ftr",notes:""};
const NEW_LEAD_TYPES=[{k:"hecm",l:"HECM",s:"existing reverse"},{k:"ftr",l:"FTR",s:"first-time reverse"},{k:"hs2",l:"HS2",s:"keep 1st mortgage"}];
function NewLeadModal({loans,setLoans,staff,onClose,onSaved}){
  const [f,setF]=useState(NEW_LEAD_BLANK);
  const [tried,setTried]=useState(false);
  const set=function(k,v){setF(function(p){var n=Object.assign({},p);n[k]=v;return n;});};
  const on=function(k){return function(e){set(k,e.target.value);};};
  const needName=!String(f.name||"").trim();
  const age=f.dob?ageFromDobStr(f.dob):null;
  const save=function(){
    setTried(true);
    if(needName)return;
    const loan=newLeadFile(f,loans,staff);
    setLoans(function(prev){return prev.concat([loan]);});
    onSaved(loan);
  };
  const field=function(label,node,wide){return(<div style={{gridColumn:wide?"1 / -1":"auto",minWidth:0}}><label style={dealLbl}>{label}</label>{node}</div>);};
  return(<div style={{position:"fixed",inset:0,background:"rgba(6,26,48,0.55)",zIndex:9000,display:"flex",alignItems:"center",justifyContent:"center",padding:12}}>
    <div role="dialog" aria-label="New lead" style={{background:C.card,borderRadius:16,padding:"24px 26px",width:"min(720px,100%)",maxHeight:"92vh",overflowY:"auto",border:"1px solid "+C.border,boxSizing:"border-box"}}>
      <div style={{display:"flex",alignItems:"flex-start",gap:12,marginBottom:6}}>
        <h3 style={{fontSize:17,fontWeight:800,color:C.text,margin:0,flex:1}}>New lead</h3>
        <button onClick={onClose} aria-label="Close" style={{border:"1px solid "+C.border,background:"transparent",color:C.textMuted,borderRadius:8,width:30,height:30,cursor:"pointer"}}>✕</button>
      </div>
      <div style={{fontSize:12,color:C.textDim,marginBottom:16,lineHeight:1.5}}>Goes on your pipeline as a Prospect. Run the numbers from the file when you're ready — the calculator opens on what you type here.</div>

      {/* HECM = refi of an existing reverse; FTR = first-time reverse; HS2 =
          FOA's HomeSafe Second behind a forward first that stays. */}
      <div role="group" aria-label="Deal type" style={{display:"inline-flex",flexWrap:"wrap",padding:4,borderRadius:12,background:C.surfaceAlt,border:"1px solid "+C.border,marginBottom:16}}>
        {NEW_LEAD_TYPES.map(function(o){
          const sel=f.dealType===o.k;
          return <button key={o.k} type="button" data-deal-type={o.k} aria-pressed={sel} onClick={function(){set("dealType",o.k);}} style={{padding:"8px 18px",borderRadius:9,border:"none",cursor:"pointer",fontFamily:"'Mulish',system-ui,sans-serif",textAlign:"left",
            background:sel?C.navy:"transparent",color:sel?C.white:C.textMuted,boxShadow:sel?"0 1px 4px rgba(11,42,74,0.25)":"none"}}>
            <div style={{fontSize:13.5,fontWeight:800,letterSpacing:.3}}>{o.l}</div>
            <div style={{fontSize:10,fontWeight:600,opacity:.75}}>{o.s}</div>
          </button>;
        })}
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:12}}>
        {field("Borrower(s) name *",<Inp placeholder="Pat & Lee Doe" value={f.name} onChange={on("name")} style={tried&&needName?{border:"1px solid "+C.red}:null}/>,true)}
        {field("Phone",<Inp placeholder="602-555-1234" value={f.phone} onChange={on("phone")}/>)}
        {field("Email",<Inp placeholder="email@example.com" value={f.email} onChange={on("email")}/>)}
        {field("Property street",<Inp placeholder="123 Main St" value={f.street} onChange={on("street")}/>,true)}
        {field("City",<Inp placeholder="Phoenix" value={f.city} onChange={on("city")}/>)}
        {field("State",<Inp placeholder="AZ" value={f.state} onChange={function(e){set("state",String(e.target.value||"").toUpperCase().slice(0,2));}}/>)}
        {field("ZIP",<Inp placeholder="85004" value={f.zip} onChange={on("zip")}/>)}
        {field("Youngest borrower's DOB"+(age?" · age "+age:""),<Inp type="date" value={f.dob} onChange={on("dob")} style={{colorScheme:"light"}}/>)}
        {field("Home value",<MoneyInp placeholder="450,000" value={f.homeValue} onChange={on("homeValue")}/>)}
        {field(f.dealType==="hs2"?"First mortgage balance (stays)":f.dealType==="hecm"?"Reverse mortgage balance":"Loan balance",<MoneyInp placeholder={f.dealType==="hs2"?"Their forward 1st — it stays":f.dealType==="hecm"?"From their statement":"0 if free and clear"} value={f.balance} onChange={on("balance")}/>)}
        {field("Notes",<textarea value={f.notes} onChange={on("notes")} placeholder="Goes on the file as a dated note — what they want, who referred them, when to call…" rows={3} style={{width:"100%",padding:"10px 14px",borderRadius:8,border:"1px solid "+C.border,background:C.surfaceAlt,color:C.text,fontSize:13,fontFamily:"'Mulish',system-ui,sans-serif",resize:"vertical",outline:"none",boxSizing:"border-box"}}/>,true)}
      </div>

      {tried&&needName&&<div role="alert" style={{marginTop:14,padding:"9px 12px",borderRadius:8,background:C.redBg,border:"1px solid "+C.red+"40",fontSize:12.5,color:C.red,fontWeight:700}}>A borrower name is the one thing the file needs.</div>}

      <div style={{display:"flex",justifyContent:"flex-end",gap:10,marginTop:18,flexWrap:"wrap"}}>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn primary onClick={save}>Add lead</Btn>
      </div>
    </div>
  </div>);
}

// ═══════════════════════════════════════════════════════════════
// ADD A DEAL — a deal priced outside the calculator
// ═══════════════════════════════════════════════════════════════
// Reece closed a HomeSafe Second in FOA's software (10/1/26): "there's
// currently no option for the ability to enter that deal in". This takes the
// lender's figures as typed and saves a normal pipeline file (lib.js
// manualDealFile): deal.manual + "Entered by hand" wherever it shows, the
// entry appended to the file's scenario history AND logged as a scenarios row
// so the history stays complete. Checks are warnings — only a name is
// required (no hard stops). The form never closes on a stray click outside.
const ADD_DEAL_BLANK={name:"",phone:"",email:"",street:"",city:"",state:"",zip:"",dob:"",homeValue:"",balance:"",
  lender:"",lenderOther:"",program:"",programOther:"",rate:"",amount:"",cash:"",comp:"",closeDate:"",stage:"Closed"};
const dealLbl={display:"block",fontSize:10,fontWeight:700,color:C.textDim,marginBottom:5,textTransform:"uppercase",letterSpacing:.5};
const dealSel={width:"100%",padding:"12px 14px",borderRadius:8,border:"1px solid "+C.border,background:C.surfaceAlt,color:C.text,fontSize:14,outline:"none",fontFamily:"'Mulish',system-ui,sans-serif",minHeight:42,boxSizing:"border-box"};
function AddDealModal({loans,setLoans,staff,onClose,onSaved}){
  const [f,setF]=useState(ADD_DEAL_BLANK);
  const [tried,setTried]=useState(false);
  const set=function(k,v){
    setF(function(p){
      var n=Object.assign({},p);n[k]=v;
      // Picking a program pre-picks its usual lender — unless a lender was
      // already chosen by hand (anything but the last program's default).
      if(k==="program"){
        var prog=MANUAL_DEAL_PROGRAMS.find(function(x){return x.name===v;});
        var prev=MANUAL_DEAL_PROGRAMS.find(function(x){return x.name===p.program;});
        if(prog&&prog.lender&&(!p.lender||(prev&&p.lender===prev.lender)))n.lender=prog.lender;
      }
      return n;
    });
  };
  const on=function(k){return function(e){set(k,e.target.value);};};
  const prog=MANUAL_DEAL_PROGRAMS.find(function(x){return x.name===f.program;})||null;
  const second=!!(prog&&prog.second);
  const margin=!!(prog&&prog.rate==="margin");
  const warnings=manualDealWarnings(f);
  const needName=!String(f.name||"").trim();
  const save=function(){
    setTried(true);
    if(needName)return;
    const built=manualDealFile(f,loans,staff);
    setLoans(function(prev){return prev.concat([built.loan]);});
    // The durable copy of the entry (fire-and-forget; scenarioStore rides out
    // the file row not being saved yet).
    logScenario(scenarioRow(built.scenario));
    onSaved(built.loan);
  };
  const field=function(label,node,wide){return(<div style={{gridColumn:wide?"1 / -1":"auto",minWidth:0}}><label style={dealLbl}>{label}</label>{node}</div>);};
  return(<div style={{position:"fixed",inset:0,background:"rgba(6,26,48,0.55)",zIndex:9000,display:"flex",alignItems:"center",justifyContent:"center",padding:12}}>
    <div role="dialog" aria-label="Add a deal" style={{background:C.card,borderRadius:16,padding:"24px 26px",width:"min(760px,100%)",maxHeight:"92vh",overflowY:"auto",border:"1px solid "+C.border,boxSizing:"border-box"}}>
      <div style={{display:"flex",alignItems:"flex-start",gap:12,marginBottom:6}}>
        <h3 style={{fontSize:17,fontWeight:800,color:C.text,margin:0,flex:1}}>Add a deal</h3>
        <button onClick={onClose} aria-label="Close" style={{border:"1px solid "+C.border,background:"transparent",color:C.textMuted,borderRadius:8,width:30,height:30,cursor:"pointer"}}>✕</button>
      </div>
      <div style={{fontSize:12,color:C.textDim,marginBottom:16,lineHeight:1.5}}>For a deal priced outside the calculator — type the lender's figures. The file is marked “{MANUAL_DEAL_LABEL}” everywhere it shows, and the entry goes into its quote history.</div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:12}}>
        {field("Borrower(s) name *",<Inp placeholder="Pat & Lee Doe" value={f.name} onChange={on("name")} style={tried&&needName?{border:"1px solid "+C.red}:null}/>,true)}
        {field("Phone",<Inp placeholder="602-555-1234" value={f.phone} onChange={on("phone")}/>)}
        {field("Email",<Inp placeholder="email@example.com" value={f.email} onChange={on("email")}/>)}
        {field("Property street",<Inp placeholder="123 Main St" value={f.street} onChange={on("street")}/>,true)}
        {field("City",<Inp placeholder="Phoenix" value={f.city} onChange={on("city")}/>)}
        {field("State",<Inp placeholder="AZ" value={f.state} onChange={function(e){set("state",String(e.target.value||"").toUpperCase().slice(0,2));}}/>)}
        {field("ZIP",<Inp placeholder="85004" value={f.zip} onChange={on("zip")}/>)}
        {field("Youngest borrower's DOB",<Inp type="date" value={f.dob} onChange={on("dob")} style={{colorScheme:"light"}}/>)}
        {field("Home value",<MoneyInp placeholder="850,000" value={f.homeValue} onChange={on("homeValue")}/>)}
        {field(second?"First lien balance":"Existing balance",<MoneyInp placeholder={second?"336,178":"0"} value={f.balance} onChange={on("balance")}/>)}
        {field("Program",<select value={f.program} onChange={on("program")} style={dealSel}>
          <option value="">Pick a program…</option>
          {MANUAL_DEAL_PROGRAMS.map(function(p){return <option key={p.name} value={p.name}>{p.name}</option>;})}
        </select>)}
        {f.program==="Other"&&field("Program name",<Inp placeholder="Program name" value={f.programOther} onChange={on("programOther")}/>)}
        {field("Lender",<select value={f.lender} onChange={on("lender")} style={dealSel}>
          <option value="">Pick a lender…</option>
          {MANUAL_DEAL_LENDERS.map(function(x){return <option key={x.value} value={x.value}>{x.label}</option>;})}
        </select>)}
        {f.lender==="Other"&&field("Lender name",<Inp placeholder="Lender name" value={f.lenderOther} onChange={on("lenderOther")}/>)}
        {field(margin?"Margin (%)":"Rate or margin (%)",<Inp placeholder={margin?"e.g. 1.75":"e.g. 9.49"} value={f.rate} onChange={on("rate")}/>)}
        {field(second?"Loan amount":"Loan amount / principal limit",<MoneyInp placeholder="212,500" value={f.amount} onChange={on("amount")}/>)}
        {field("Cash to borrower",<MoneyInp placeholder="e.g. 150,000" value={f.cash} onChange={on("cash")}/>)}
        {field("Your comp",<MoneyInp placeholder="e.g. 7,500" value={f.comp} onChange={on("comp")}/>)}
        {field("Close date (optional)",<Inp type="date" value={f.closeDate} onChange={on("closeDate")} style={{colorScheme:"light"}}/>)}
        {field("Stage",<select value={f.stage} onChange={on("stage")} style={dealSel}>
          {STAGES.map(function(s){return <option key={s} value={s}>{s}</option>;})}
        </select>)}
      </div>

      {tried&&needName&&<div role="alert" style={{marginTop:14,padding:"9px 12px",borderRadius:8,background:C.redBg,border:"1px solid "+C.red+"40",fontSize:12.5,color:C.red,fontWeight:700}}>A borrower name is the one thing the file needs.</div>}
      {warnings.length>0&&<div style={{marginTop:14,padding:"10px 14px",borderRadius:8,background:C.orangeBg,border:"1px solid "+C.orange+"40"}}>
        <div style={{fontSize:11,fontWeight:800,color:C.orange,textTransform:"uppercase",letterSpacing:.5,marginBottom:4}}>Worth a look — you can still save</div>
        {warnings.map(function(w){return <div key={w} style={{fontSize:12,color:C.text,lineHeight:1.55}}>• {w}</div>;})}
      </div>}

      <div style={{display:"flex",justifyContent:"flex-end",gap:10,marginTop:18,flexWrap:"wrap"}}>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn primary onClick={save}>Save deal</Btn>
      </div>
    </div>
  </div>);
}

// ═══════════════════════════════════════════════════════════════
// FALLOUT BOARD
// ═══════════════════════════════════════════════════════════════
// Where a worked deal goes when it doesn't get pitched. This is the thing the
// old archive button should have been: instead of one boolean and a file that
// vanishes, every fallout row shows WHY it fell out, WHAT the numbers were
// when it did, and WHEN to try again — and puts a one-click call back on it.
//
// Qualified fallout is the money list: those are deals that priced fine and
// just need the calendar to turn. Denied is kept so the same dead deal never
// gets re-run from scratch six months later.
// One board for BOTH staff — every row carries its owner chip. It shows
// everyone's fallout unless narrowed on this board (`inView`, null = all);
// the pipeline's remembered Mine/All choice never applies here. Max's board
// is handed his files alone (showOwner false: no chips, they're all his).
function FalloutBoard({loans,setLoans,go,onStartCall,rateSheets,inView,staff,ownerLabel,onShowAll,showOwner}){
  const me=STAFF[staff]?staff:"reece";
  const swapNow=currentSwap(rateSheets);
  const [tab,setTab]=useState(FALLOUT_QUAL);
  const [q,setQ]=useState("");
  const shown=useMemo(function(){return inView?loans.filter(inView):loans;},[loans,inView]);
  const hiddenByOwner=useMemo(function(){return inView?loans.filter(function(l){return l.stage===tab&&!inView(l);}).length:0;},[loans,inView,tab]);
  const rows=useMemo(function(){
    const needle=q.trim().toLowerCase();
    return shown
      .filter(function(l){return l.stage===tab;})
      .filter(function(l){
        if(!needle)return true;
        return [l.b,l.prop,l.ph,l.falloutReason,l.id].join(" ").toLowerCase().indexOf(needle)>=0;
      })
      .sort(function(a,b){
        // Anything due for a call back floats to the top, oldest first.
        const ad=a.followUpDate?String(a.followUpDate).slice(0,10):"9999";
        const bd=b.followUpDate?String(b.followUpDate).slice(0,10):"9999";
        if(ad!==bd)return ad<bd?-1:1;
        return String(b.falloutAt||"").localeCompare(String(a.falloutAt||""));
      });
  },[shown,tab,q]);

  const counts=useMemo(function(){
    const out={};
    FALLOUT_STAGES.forEach(function(s){out[s]=shown.filter(function(l){return l.stage===s;}).length;});
    return out;
  },[shown]);

  const revive=function(l){
    if(!window.confirm("Put "+l.b+" ("+l.id+") back on the active pipeline as a Prospect?\n\nEverything on the file — notes, the numbers you ran, the fallout reason — is kept."))return;
    const ts=new Date();
    setLoans(function(prev){return prev.map(function(x){
      if(x.id!==l.id)return x;
      // Reviving is working the file — it claims an unowned one.
      return claimLoan(Object.assign({},x,{stage:"Prospect",archived:false,queue:"numbers",queueAt:ts.toISOString(),
        activityLog:(x.activityLog||[]).concat([{text:"Revived from "+l.stage+" back onto the pipeline",type:"stage",
          timestamp:ts.toLocaleString(),date:ts.toLocaleDateString(),time:ts.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}),epoch:ts.getTime()}])}),me);
    });});
  };

  const $=function(n){return n||n===0?"$"+Math.round(n).toLocaleString():"—";};
  // The interest dot on each row (src/interest.js) — same save as the board.
  const setInterest=function(id,patch){setLoans(function(prev){return applyInterest(prev,id,patch);});};

  return(<div>
    <div style={{display:"flex",gap:10,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
      {FALLOUT_STAGES.map(function(s){
        const on=tab===s;const col=s===FALLOUT_QUAL?C.gold:s===FALLOUT_DENIED?C.red:C.cyan;
        return <button key={s} onClick={function(){setTab(s);}} style={{padding:"9px 16px",borderRadius:9,cursor:"pointer",fontSize:12.5,fontWeight:800,fontFamily:"'Mulish',system-ui,sans-serif",
          border:"1px solid "+(on?col:C.border),background:on?col+"15":"transparent",color:on?col:C.textMuted}}>
          {s} <span style={{opacity:.7,fontWeight:700}}>({counts[s]||0})</span>
        </button>;
      })}
      <input value={q} onChange={function(e){setQ(e.target.value);}} placeholder="Filter by name, address, phone, reason…"
        style={{flex:"1 1 240px",padding:"9px 13px",borderRadius:8,border:"1px solid "+(q?C.accent:C.border),background:C.surfaceAlt,color:C.text,fontSize:12.5,outline:"none",fontFamily:"'Mulish',system-ui,sans-serif",boxSizing:"border-box"}}/>
    </div>

    <div style={{fontSize:11.5,color:C.textDim,marginBottom:14,lineHeight:1.55,maxWidth:760}}>
      {tab===FALLOUT_QUAL
        ? "Deals that priced fine but weren't ready. Everything you ran is still on the file — call back on the date and pick up where you left off."
        : tab===FALLOUT_DENIED
        ? "Deals that don't work, with the reason recorded so you never re-run the same dead file. Revive one if something changes (value, balance, birthday)."
        : "Calls that never got priced — a quick question, someone fishing for information, the wrong person. Nobody ever established whether these qualify, which makes them the cheapest list you own: real people who picked up the phone about a reverse mortgage."}
    </div>

    {rows.length===0?
      <div style={{padding:"34px 20px",textAlign:"center",border:"1px dashed "+C.border,borderRadius:12,color:C.textDim,fontSize:13}}>
        Nothing in {tab} yet{q?" matching “"+q+"”":""}{inView?" among "+(ownerLabel||"these")+" files":""}. {onStartCall?"Calls land here from the New Call console.":"Files land here when you fall them out from the file's page."}
        {hiddenByOwner>0&&onShowAll&&<div style={{marginTop:10}}><Btn small onClick={onShowAll} style={{color:C.accentLight}}>Show all ({hiddenByOwner} more)</Btn></div>}
      </div>:
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {rows.map(function(l){
          const due=isDueBy(l.followUpDate);
          // A HomeSafe Second deal (the card's HS2 pick or an HS2 pitch): the
          // balance is the first mortgage that stays, not a payoff.
          const hs2=isHs2File(l);
          // The last time this file was priced — a quote run OR a pitch, the
          // NEWER of the two by time. (A Loan Detail → Calculate re-pitch adds
          // scenarioHistory but no quoteLog entry, so "quote first" showed a
          // stale market.) Older files predate rate stamping and show nothing.
          const lastQuote=(l.quoteLog||[]).slice(-1)[0];
          const lastPitch=(l.scenarioHistory||[]).slice(-1)[0];
          const qEpoch=lastQuote?(lastQuote.epoch||(lastQuote.at?Date.parse(lastQuote.at)||0:0)):-1;
          const pEpoch=lastPitch?(lastPitch.timestamp?(Date.parse(lastPitch.timestamp)||0):(lastPitch.epoch||0)):-1;
          const lastRun=(lastPitch&&(!lastQuote||pEpoch>qEpoch))
            ?{label:lastPitch.manual?"Last deal ("+MANUAL_DEAL_LABEL.toLowerCase()+")":"Last pitched",date:lastPitch.date,what:(lastPitch.product||"a scenario")+(lastPitch.payOnly
              // HomeSafe Second mode (PL in, pay out): FOA's amount + the pay, never costs / cash.
              ?", FOA's "+$(lastPitch.grossPL)+" · your pay "+payCents(lastPitch.pay)
              :(lastPitch.cashToBorrower>0?", "+$(lastPitch.cashToBorrower)+" available":"")+(!lastPitch.manual&&Number(lastPitch.lenderAmount)>0?" (on the lender's amount)":"")),rateEnv:lastPitch.rateEnv}
            :lastQuote?{label:"Last priced",date:lastQuote.date,what:(lastQuote.top||[])[0]?(lastQuote.top[0].name+", "+$(lastQuote.top[0].avail)+" available"):(lastQuote.productCount+" products"),rateEnv:lastQuote.rateEnv}
            :null;
          const stamped=lastRun&&lastRun.rateEnv?lastRun.rateEnv:null;
          const ranAtSwap=stamped&&!isNaN(parseFloat(stamped.swapRate))?parseFloat(stamped.swapRate):null;
          const swapDrop=(ranAtSwap!=null&&swapNow!=null)?Number((ranAtSwap-swapNow).toFixed(3)):0;
          const ist=interestStyle(l);
          return(<div key={l.id} data-fallout-row={l.id} style={{background:ist.booked?C.booked:C.card,border:"1px solid "+(due?C.orange+"60":ist.booked?C.bookedEdge:C.border),borderRadius:12,padding:"14px 16px"}}>
            <div style={{display:"flex",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
              <div style={{flex:"1 1 260px",minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                  {showOwner!==false&&<OwnerChip owner={ownerOf(l)}/>}
                  <InterestMenu loan={l} onSet={function(patch){setInterest(l.id,patch);}}/>
                  <span data-interest-name={l.id} title={ist.label||undefined} onClick={function(){go&&go(l);}} style={Object.assign({fontSize:14,fontWeight:800,color:C.text,cursor:"pointer"},interestNameStyle(l))}>{l.b}</span>
                  <span style={{fontSize:10.5,color:C.textDim}}>{l.id}</span>
                  {hs2&&<Badge label={HS2_LOAN_TYPE} color={C.navy} bg={C.navy+"14"}/>}
                  {due&&<Badge label="CALL BACK DUE" color={C.orange} bg={C.orangeBg}/>}
                </div>
                <div style={{fontSize:11.5,color:C.textDim,marginTop:3}}>{l.prop||"no address"}</div>
                <div style={{fontSize:12,color:C.text,marginTop:7,fontWeight:600}}>
                  {l.falloutReason||"no reason recorded"}
                </div>
                {l.followUpNote&&<div style={{fontSize:11.5,color:C.textMuted,marginTop:3}}>{l.followUpNote}</div>}
              </div>

              <div style={{flex:"0 1 260px",minWidth:190,fontSize:11.5,color:C.textMuted,lineHeight:1.7}}>
                <div>Home {$(l.hv)} · {hs2?"1st mortgage "+$(l.mb)+" (stays)":"owes "+$(l.mb)}{loanAge(l)?" · age "+loanAge(l):""}</div>
                {lastRun&&<div>{lastRun.label} {lastRun.date} — {lastRun.what}</div>}
                {/* What the market was when this fell out, against where it is
                    now. A lower swap today means a higher PLF on the same
                    house — that is the whole reason to call them back. */}
                {ranAtSwap!=null&&<div style={{color:swapDrop>0?C.green:C.textDim,fontWeight:swapDrop>0?700:400}}>
                  Run at {ranAtSwap.toFixed(3)}% swap
                  {swapNow!=null?(swapDrop>0
                    ? " · market is "+swapDrop.toFixed(3)+"% lower today"
                    : swapDrop<0 ? " · market is "+Math.abs(swapDrop).toFixed(3)+"% higher today" : " · unchanged"):""}
                </div>}
                {l.falloutAt&&<div style={{color:C.textDim}}>Fell out {fmtWhen(l.falloutAt)}</div>}
                {l.followUpDate&&<div style={{color:due?C.orange:C.textDim,fontWeight:due?700:400}}>Call back {String(l.followUpDate).slice(0,10)}</div>}
              </div>

              <div style={{display:"flex",gap:7,alignItems:"center",flexWrap:"wrap",flexShrink:0}}>
                {l.ph&&<CallBtn phone={l.ph} small/>}
                {onStartCall&&<Btn small primary onClick={function(){onStartCall({prospect:prospectFromLoan(l)});}}>{Ic.phone} Work it</Btn>}
                <Btn small onClick={function(){revive(l);}} style={{color:C.green}}>↩ Back to pipeline</Btn>
                <Btn small onClick={function(){go&&go(l);}} style={{color:C.textDim}}>Open file</Btn>
              </div>
            </div>
          </div>);
        })}
      </div>}
  </div>);
}

export { Pipeline, FalloutBoard, hoverCardPos, isRowControl, HOVER_DELAY_MS, SORT_KEY, SORT_OPTIONS, DEFAULT_SORT };
